import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  PayrollAddOn,
  PayrollAdvance,
  PayrollCycle,
  PayrollRun,
  UpsertPayrollCycleInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { payoutKeys } from '../payouts/use-payouts.js';
import {
  getPastPayrollRuns,
  getPayrollCycle,
  getPayrollRun,
  recordPayrollAddOn,
  recordPayrollAdvance,
  upsertPayrollCycle,
  type RecordPayrollAddOnBody,
  type RecordPayrollAdvanceBody,
} from './payroll-api.js';

/**
 * The payroll screen's data layer.
 *
 * NOTHING HERE GOES THROUGH THE OFFLINE WRITE QUEUE, for the same reason nothing in
 * `use-payouts.ts` does: an advance is cash leaving a drawer, and replaying one silently twenty
 * minutes later — against a cycle that may have been run from another phone in between — is a
 * far worse failure than "you are offline, try again".
 *
 * Every write runs the SAME invalidation, and that invalidation reaches beyond payroll: an
 * advance changes what a person takes home this cycle AND what the settlement screens show, so
 * `payoutKeys.all` goes with it. One helper, so no mutation can be added later that forgets.
 */

/** How many closed cycles the Past tab walks back over. */
export const PAST_PAYROLL_CYCLE_LIMIT = 6;

export const payrollKeys = {
  all: ['payroll'] as const,
  cycle: () => ['payroll', 'cycle'] as const,
  run: (on?: string) => ['payroll', 'run', on ?? 'current'] as const,
  history: (periodFrom: string, limit: number) =>
    ['payroll', 'history', periodFrom, limit] as const,
};

export function useInvalidatePayroll(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: payrollKeys.all });
    // An advance moves what the settlement screens show, so they are stale too.
    void queryClient.invalidateQueries({ queryKey: payoutKeys.all });
  };
}

export function usePayrollCycle(options: { enabled?: boolean } = {}): UseQueryResult<PayrollCycle> {
  const client = useApiClient();
  return useQuery({
    queryKey: payrollKeys.cycle(),
    queryFn: () => getPayrollCycle(client),
    enabled: options.enabled ?? true,
  });
}

/** `on` omitted is the cycle the shop is in right now. */
export function usePayrollRun(
  options: { on?: string; enabled?: boolean } = {},
): UseQueryResult<PayrollRun> {
  const client = useApiClient();
  return useQuery({
    queryKey: payrollKeys.run(options.on),
    queryFn: () => getPayrollRun(client, options.on ? { on: options.on } : {}),
    enabled: options.enabled ?? true,
  });
}

/** The cycles that closed before the one anchored at `periodFrom`, newest first. */
export function usePayrollRunHistory(
  periodFrom: string | null,
  options: { enabled?: boolean; limit?: number } = {},
): UseQueryResult<PayrollRun[]> {
  const client = useApiClient();
  const limit = options.limit ?? PAST_PAYROLL_CYCLE_LIMIT;
  return useQuery({
    queryKey: payrollKeys.history(periodFrom ?? '', limit),
    queryFn: () => getPastPayrollRuns(client, periodFrom as string, limit),
    enabled: periodFrom !== null && (options.enabled ?? true),
  });
}

export function useUpsertPayrollCycle(): UseMutationResult<
  PayrollCycle,
  unknown,
  UpsertPayrollCycleInput
> {
  const client = useApiClient();
  const invalidate = useInvalidatePayroll();
  return useMutation({
    mutationFn: (input: UpsertPayrollCycleInput) => upsertPayrollCycle(client, input),
    onSuccess: invalidate,
  });
}

export function useRecordPayrollAdvance(
  staffUserId: string,
): UseMutationResult<PayrollAdvance, unknown, RecordPayrollAdvanceBody> {
  const client = useApiClient();
  const invalidate = useInvalidatePayroll();
  return useMutation({
    mutationFn: (input: RecordPayrollAdvanceBody) =>
      recordPayrollAdvance(client, staffUserId, input),
    onSuccess: invalidate,
  });
}

export function useRecordPayrollAddOn(
  staffUserId: string,
): UseMutationResult<PayrollAddOn, unknown, RecordPayrollAddOnBody> {
  const client = useApiClient();
  const invalidate = useInvalidatePayroll();
  return useMutation({
    mutationFn: (input: RecordPayrollAddOnBody) => recordPayrollAddOn(client, staffUserId, input),
    onSuccess: invalidate,
  });
}
